import { useEffect, useState } from 'react';
import { Select } from '@hm-group/bads-react';
import type { SelectOption } from '@hm-group/bads-react';
import { environment } from '../../environments/environment';
import type {
  CorporateBrand,
  CustomerGroup,
  Division,
  Section,
  Department,
} from '../../models/organization';
import {
  ORGANIZATION_CORPORATEBRAND_API,
  ORGANIZATION_CUSTOMERGROUP_API,
  ORGANIZATION_DIVISION_API,
  ORGANIZATION_SECTION_API,
  ORGANIZATION_DEPARTMENT_API,
} from '../../models';
import { httpGet } from '../../services/httpService';
import { useAppContext } from '../../context/AppContext';
import { LS, lsGet, lsGetLabel, lsSet, lsSetLabel, lsClear } from '../../constants/constants';
import styles from './SearchBar.module.css';

export function SearchBar() {
  const { setSearchBarData, setSelectedDepartmentInfo, setEnablePublishBtn } = useAppContext();
  const apiUri = environment.apiConfig.articleplan_api.uri;

  // Raw data from API
  const [brands, setBrands] = useState<CorporateBrand[]>([]);
  const [allCustomerGroups, setAllCustomerGroups] = useState<CustomerGroup[]>([]);
  const [allDivisions, setAllDivisions] = useState<Division[]>([]);
  const [allSections, setAllSections] = useState<Section[]>([]);
  const [allDepartments, setAllDepartments] = useState<Department[]>([]);

  // Selected IDs - restored from localStorage on mount
  const [selectedBrandId, setSelectedBrandId] = useState(() => lsGet(LS.brand));
  const [selectedCgId, setSelectedCgId] = useState(() => lsGet(LS.customerGroup));
  const [selectedDivId, setSelectedDivId] = useState(() => lsGet(LS.division));
  const [selectedSecId, setSelectedSecId] = useState(() => lsGet(LS.section));
  const [selectedDeptId, setSelectedDeptId] = useState(() => lsGet(LS.department));

  // Derived filtered options
  const customerGroups = allCustomerGroups.filter(
    (cg) => !selectedBrandId || cg.corporateBrandId.toString() === selectedBrandId,
  );
  const divisions = allDivisions.filter(
    (d) =>
      (!selectedBrandId || d.corporateBrandId.toString() === selectedBrandId) &&
      (!selectedCgId || d.customerGroupId.toString() === selectedCgId),
  );
  const sections = allSections.filter(
    (s) => !selectedDivId || s.divisionId.toString() === selectedDivId,
  );
  const departments = allDepartments.filter(
    (d) => !selectedSecId || d.sectionId.toString() === selectedSecId,
  );

  // Provide a seed option from localStorage while API data is still loading
  const withSeed = (options: SelectOption[], selectedId: string, lsKey: string): SelectOption[] => {
    if (options.length > 0 || !selectedId) return options;
    const label = lsGetLabel(lsKey);
    return label ? [{ value: selectedId, label, id: selectedId }] : options;
  };

  // Convert to BADS SelectOption[]
  const brandOptions: SelectOption[] = withSeed(
    brands.map((b) => ({
      value: b.corporateBrandId.toString(),
      label: b.corporateBrandShortName,
      id: b.corporateBrandId.toString(),
    })),
    selectedBrandId,
    LS.brand,
  );
  const cgOptions: SelectOption[] = withSeed(
    customerGroups
      .slice()
      .sort((a, b) => a.customerGroupSortOrder - b.customerGroupSortOrder)
      .map((cg) => ({
        value: cg.customerGroupId.toString(),
        label: `${cg.customerGroupNumber} - ${cg.customerGroupName}`,
        id: cg.customerGroupId.toString(),
      })),
    selectedCgId,
    LS.customerGroup,
  );
  const divOptions: SelectOption[] = withSeed(
    divisions
      .slice()
      .sort((a, b) => a.divisionSortOrder - b.divisionSortOrder)
      .map((d) => ({
        value: d.divisionId.toString(),
        label: `${d.divisionNumber} - ${d.divisionName}`,
        id: d.divisionId.toString(),
      })),
    selectedDivId,
    LS.division,
  );
  const secOptions: SelectOption[] = withSeed(
    sections
      .slice()
      .sort((a, b) => a.sectionSortOrder - b.sectionSortOrder)
      .map((s) => ({
        value: s.sectionId.toString(),
        label: `${s.sectionNumber} - ${s.sectionName}`,
        id: s.sectionId.toString(),
      })),
    selectedSecId,
    LS.section,
  );
  const deptOptions: SelectOption[] = withSeed(
    departments
      .slice()
      .sort((a, b) => Number(a.departmentNumber) - Number(b.departmentNumber))
      .map((d) => ({
        value: d.departmentId.toString(),
        label: `${d.departmentNumber} - ${d.departmentName}`,
        id: d.departmentId.toString(),
      })),
    selectedDeptId,
    LS.department,
  );

  // Fetch all data on mount – division first (populates backend cache), then rest in parallel
  useEffect(() => {
    httpGet<Division[]>(`${apiUri}${ORGANIZATION_DIVISION_API}`)
      .then((divs) =>
        Promise.all([
          httpGet<CorporateBrand[]>(`${apiUri}${ORGANIZATION_CORPORATEBRAND_API}`),
          httpGet<CustomerGroup[]>(`${apiUri}${ORGANIZATION_CUSTOMERGROUP_API}`),
          httpGet<Section[]>(`${apiUri}${ORGANIZATION_SECTION_API}`),
          httpGet<Department[]>(`${apiUri}${ORGANIZATION_DEPARTMENT_API}`),
        ]).then(([brs, cgs, secs, depts]) => ({ brs, cgs, divs, secs, depts })),
      )
      .then(({ brs, cgs, divs, secs, depts }) => {
        // Set state in hierarchy order: Brand → CG → Division → Section → Department
        setBrands(brs);
        setAllCustomerGroups(cgs);
        setAllDivisions(divs);
        setAllSections(secs);
        setAllDepartments(depts);

        // Refresh stored labels from API data (keeps labels fresh & handles first-time migration)
        const savedBrand = brs.find((b) => b.corporateBrandId.toString() === lsGet(LS.brand));
        if (savedBrand) lsSetLabel(LS.brand, savedBrand.corporateBrandShortName);
        const savedCg = cgs.find((c) => c.customerGroupId.toString() === lsGet(LS.customerGroup));
        if (savedCg) lsSetLabel(LS.customerGroup, `${savedCg.customerGroupNumber} - ${savedCg.customerGroupName}`);
        const savedDiv = divs.find((d) => d.divisionId.toString() === lsGet(LS.division));
        if (savedDiv) lsSetLabel(LS.division, `${savedDiv.divisionNumber} - ${savedDiv.divisionName}`);
        const savedSec = secs.find((s) => s.sectionId.toString() === lsGet(LS.section));
        if (savedSec) lsSetLabel(LS.section, `${savedSec.sectionNumber} - ${savedSec.sectionName}`);
        const savedDept = depts.find((d) => d.departmentId.toString() === lsGet(LS.department));
        if (savedDept) lsSetLabel(LS.department, `${savedDept.departmentNumber} - ${savedDept.departmentName}`);

      // Auto-select brand if only one and nothing saved
      if (brs.length === 1 && !lsGet(LS.brand)) {
        const brandId = brs[0].corporateBrandId.toString();
        setSelectedBrandId(brandId);
        lsSet(LS.brand, brandId);
        lsSetLabel(LS.brand, brs[0].corporateBrandShortName);
      }

      // Restore workspace from previously saved selection
      const savedDeptId = lsGet(LS.department);
      if (savedDeptId) {
        const deptInfo = depts.find((d) => d.departmentId.toString() === savedDeptId) ?? null;
        setSelectedDepartmentInfo(deptInfo);
        setSearchBarData({
          brand: lsGet(LS.brand),
          customerGroup: lsGet(LS.customerGroup),
          departments: [savedDeptId],
          division: lsGet(LS.division),
          section: lsGet(LS.section),
          showWorkspace: true,
        });
      } else if (lsGet(LS.section)) {
        // Section-only selection (no department) – restore so SelectionBar appears
        setSearchBarData({
          brand: lsGet(LS.brand),
          customerGroup: lsGet(LS.customerGroup),
          departments: [],
          division: lsGet(LS.division),
          section: lsGet(LS.section),
          showWorkspace: false,
        });
      }
    }).catch(() => { /* Interceptor shows error toast */ });
  }, [apiUri, setSearchBarData, setSelectedDepartmentInfo]);

  const handleBrandChange = (option: SelectOption) => {
    const id = (option as { value: string } | null)?.value ?? '';
    setSelectedBrandId(id);
    setSelectedCgId('');
    setSelectedDivId('');
    setSelectedSecId('');
    setSelectedDeptId('');
    setSelectedDepartmentInfo(null);
    setEnablePublishBtn(false);
    lsSet(LS.brand, id);
    lsSetLabel(LS.brand, (option as { label: string } | null)?.label ?? '');
    lsClear(LS.customerGroup, LS.division, LS.section, LS.department);
    setSearchBarData({ brand: id, customerGroup: undefined, departments: [], division: undefined, section: undefined, showWorkspace: false });
  };

  const handleCgChange = (option: SelectOption) => {
    const id = (option as { value: string } | null)?.value ?? '';
    setSelectedCgId(id);
    setSelectedDivId('');
    setSelectedSecId('');
    setSelectedDeptId('');
    setSelectedDepartmentInfo(null);
    setEnablePublishBtn(false);
    lsSet(LS.customerGroup, id);
    lsSetLabel(LS.customerGroup, (option as { label: string } | null)?.label ?? '');
    lsClear(LS.division, LS.section, LS.department);
    setSearchBarData({ brand: selectedBrandId, customerGroup: id, departments: [], division: undefined, section: undefined, showWorkspace: false });
  };

  const handleDivChange = (option: SelectOption) => {
    const id = (option as { value: string } | null)?.value ?? '';
    setSelectedDivId(id);
    setSelectedSecId('');
    setSelectedDeptId('');
    setSelectedDepartmentInfo(null);
    setEnablePublishBtn(false);
    lsSet(LS.division, id);
    lsSetLabel(LS.division, (option as { label: string } | null)?.label ?? '');
    lsClear(LS.section, LS.department);
    setSearchBarData({ brand: selectedBrandId, customerGroup: selectedCgId, departments: [], division: id, section: undefined, showWorkspace: false });
  };

  const handleSecChange = (option: SelectOption) => {
    const id = (option as { value: string } | null)?.value ?? '';
    setSelectedSecId(id);
    setSelectedDeptId('');
    setSelectedDepartmentInfo(null);
    setEnablePublishBtn(false);
    lsSet(LS.section, id);
    lsSetLabel(LS.section, (option as { label: string } | null)?.label ?? '');
    lsClear(LS.department);
    setSearchBarData({ brand: selectedBrandId, customerGroup: selectedCgId, departments: [], division: selectedDivId, section: id, showWorkspace: false });
  };

  const handleDeptChange = (option: SelectOption) => {
    const id = (option as { value: string } | null)?.value ?? '';
    setSelectedDeptId(id);
    const deptInfo = allDepartments.find((d) => d.departmentId.toString() === id) ?? null;
    setSelectedDepartmentInfo(deptInfo);
    lsSet(LS.department, id);
    lsSetLabel(LS.department, (option as { label: string } | null)?.label ?? '');
    setSearchBarData({
      brand: selectedBrandId,
      customerGroup: selectedCgId,
      departments: id ? [id] : [],
      division: selectedDivId,
      section: selectedSecId,
      showWorkspace: !!id,
    });
  };

  const handleDeptClear = () => {
    setSelectedDeptId('');
    setSelectedDepartmentInfo(null);
    setEnablePublishBtn(false);
    lsClear(LS.department);
    setSearchBarData({ brand: selectedBrandId, customerGroup: selectedCgId, departments: [], division: selectedDivId, section: selectedSecId, showWorkspace: false });
  };

  const showBrandSelect = brands.length > 1;

  return (
    <div className={styles.searchBarContainer}>
      <div className={styles.searchForm}>
        <div className={styles.verticalForm}>
          {showBrandSelect && (
            <div className={styles.formControl}>
              <Select
                options={brandOptions}
                value={selectedBrandId}
                onChange={handleBrandChange}
                label="Brand"
                labelSize="bold"
                placeholder="Select a brand"
                size="normal"
                searchable={false}
              />
            </div>
          )}
          <div className={styles.formControl}>
            <Select
              options={cgOptions}
              value={selectedCgId}
              onChange={handleCgChange}
              label="Customer group"
              labelSize="bold"
              placeholder="Select a customer group"
              size="normal"
              disabled={!selectedBrandId}
              searchable={false}
            />
          </div>
          <div className={styles.formControl}>
            <Select
              options={divOptions}
              value={selectedDivId}
              onChange={handleDivChange}
              label="Division"
              labelSize="bold"
              placeholder="Select a division"
              size="normal"
              disabled={!selectedCgId}
              searchable={false}
            />
          </div>
          <div className={styles.formControl}>
            <Select
              options={secOptions}
              value={selectedSecId}
              onChange={handleSecChange}
              label="Section"
              labelSize="bold"
              placeholder="Select a section"
              size="normal"
              disabled={!selectedDivId}
              searchable={false}
            />
          </div>
          <div className={styles.formControl}>
            <Select
              options={deptOptions}
              value={selectedDeptId}
              onChange={handleDeptChange}
              onClear={handleDeptClear}
              label="Department"
              labelSize="bold"
              placeholder="Select a department"
              size="normal"
              disabled={!selectedSecId}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
